import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ReceiptText, Gauge, BadgeDollarSign, Send, Check, Upload, Download } from "lucide-react";
import api, { apiError, downloadBlob } from "@/lib/api";
import Modal from "@/components/Modal";
import Field from "@/components/Field";
import DocPreview from "@/components/DocPreview";
import StatusBadge from "@/components/StatusBadge";
import { fmtAUD, fmtAUDex, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const MAX_CLAIM_DOCS = 5;

function NewClaimDialog({ project, claims, variations, onClose, onSaved }) {
  const prevPct = claims.length ? claims[claims.length - 1].progress_pct : 0;
  const [form, setForm] = useState({
    claim_date: new Date().toISOString().slice(0, 10),
    progress_pct: prevPct,
    invoice_reference: "", invoice_date: "", notes: "",
  });
  const [selectedVars, setSelectedVars] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const claimable = variations.filter((v) => v.status === "Approved" && !v.claimed);
  const base = project.base_contract_value || 0;
  const pct = parseFloat(form.progress_pct || 0);
  const baseAmount = Math.max(0, (pct - prevPct) / 100) * base;
  const varAmount = claimable.filter((v) => selectedVars.includes(v.id)).reduce((s, v) => s + v.amount, 0);

  const toggleVar = (id) =>
    setSelectedVars((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/claims`, {
        claim_date: form.claim_date,
        progress_pct: pct,
        variation_ids: selectedVars,
        invoice_reference: form.invoice_reference,
        invoice_date: form.invoice_date || null,
        notes: form.notes,
      });
      toast.success("Progress claim recorded");
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title="New Progress Claim" onClose={onClose} wide>
      <form onSubmit={submit} data-testid="claim-form">
        <p className="text-[11px] text-gray-400 mb-3">All values Excl. GST</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Claim Date"><input data-testid="claim-date" type="date" className="bdx-input" value={form.claim_date} onChange={set("claim_date")} /></Field>
          <Field label={`Base Contract Progress % (currently ${prevPct}%)`}>
            <input data-testid="claim-pct" type="number" min={prevPct} max="100" step="0.1" className="bdx-input font-mono-data" value={form.progress_pct} onChange={set("progress_pct")} />
          </Field>
          <div className="rounded-lg border border-gray-200 bg-[#faf9f8] p-3 shadow-sm">
            <div className="bdx-label">This Claim — Base Contract</div>
            <div data-testid="claim-base-preview" className="font-mono-data font-semibold text-gray-900">{fmtAUDex(baseAmount)}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">{prevPct}% → {pct || 0}% of {fmtAUDex(base)}</div>
          </div>
        </div>

        <div className="mt-4">
          <div className="bdx-label mb-2">Include Approved Variations (claimed at 100% of approved value)</div>
          {claimable.length === 0 ? (
            <p className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-xs text-gray-400">No approved unclaimed variations available</p>
          ) : (
            <div className="space-y-1.5">
              {claimable.map((v) => (
                <label key={v.id} className="flex cursor-pointer items-center justify-between rounded-md border border-gray-200 px-3 py-2 transition-colors hover:border-[#d9cde5] hover:bg-[#faf8fc]">
                  <span className="flex items-center gap-2.5 text-sm">
                    <input data-testid={`claim-var-${v.code}`} type="checkbox" className="h-4 w-4 accent-[#604184]" checked={selectedVars.includes(v.id)} onChange={() => toggleVar(v.id)} />
                    <span className="font-mono-data font-medium">{v.code}</span>
                    <span className="text-gray-600 truncate max-w-xs">{v.description}</span>
                  </span>
                  <span className="font-mono-data text-sm">{fmtAUDex(v.amount)}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Invoice Reference (optional)"><input data-testid="claim-invoice-ref" className="bdx-input font-mono-data" value={form.invoice_reference} onChange={set("invoice_reference")} /></Field>
          <Field label="Invoice Date (optional)"><input data-testid="claim-invoice-date" type="date" className="bdx-input" value={form.invoice_date} onChange={set("invoice_date")} /></Field>
          <div className="rounded-lg border border-[#d9cde5] bg-[#f3eff7] p-3 shadow-sm">
            <div className="text-[10px] font-semibold uppercase text-[#604184]" style={{ letterSpacing: "0.1em" }}>Total This Claim (Excl. GST)</div>
            <div data-testid="claim-total-preview" className="mt-1 font-mono-data text-lg font-bold text-gray-950">{fmtAUDex(baseAmount + varAmount)}</div>
          </div>
        </div>
        <Field label="Notes" className="mt-4"><textarea data-testid="claim-notes" className="bdx-input" rows={2} value={form.notes} onChange={set("notes")} /></Field>

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
          <button type="button" data-testid="claim-cancel" className="bdx-btn-secondary" onClick={onClose}>Cancel</button>
          <button data-testid="claim-submit" disabled={busy} className="bdx-btn-primary">{busy ? "Recording…" : "Record Claim"}</button>
        </div>
      </form>
    </Modal>
  );
}

function PaymentCell({ claim, readOnly, onChanged, onReceive }) {
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await api.post(`/claims/${claim.id}/send`, {});
      toast.success(`${claim.code} marked sent — payment now outstanding`);
      onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (claim.paid) {
    return <span data-testid={`claim-paid-${claim.code}`} className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-semibold uppercase text-emerald-700" style={{ letterSpacing: "0.05em" }}><Check size={11} strokeWidth={2.2} /> Received · {fmtDate(claim.paid_at)}</span>;
  }
  if (claim.sent) {
    if (readOnly) {
      return <span data-testid={`claim-sent-${claim.code}`} className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-semibold uppercase text-emerald-700" style={{ letterSpacing: "0.05em" }}><Send size={11} strokeWidth={1.9} /> Sent</span>;
    }
    return <button data-testid={`claim-sent-${claim.code}`} className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-semibold uppercase text-emerald-700 transition-colors hover:bg-emerald-100" style={{ letterSpacing: "0.05em" }} title={`Record payment receipt for ${claim.code}`} onClick={(e) => { e.stopPropagation(); onReceive(claim); }}><Send size={11} strokeWidth={1.9} /> Sent</button>;
  }
  if (readOnly) {
    return <span data-testid={`claim-notsent-${claim.code}`} className="inline-flex whitespace-nowrap rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[10px] font-semibold uppercase text-gray-500" style={{ letterSpacing: "0.05em" }}>Not Sent</span>;
  }
  return <button data-testid={`claim-send-${claim.code}`} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded border border-amber-300 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold uppercase text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50" style={{ letterSpacing: "0.05em" }} disabled={busy} onClick={send}><Send size={12} strokeWidth={1.9} /> Send</button>;
}

function ReceiptDialog({ claim, onClose, onSaved }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/claims/${claim.id}/receive`, { received_date: date });
      toast.success(`${claim.code} payment receipt recorded`);
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Record Payment Receipt — ${claim.code}`} onClose={onClose}>
      <form onSubmit={confirm} data-testid="claim-receipt-form">
        <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Claim total (Excl. GST)</span><span className="font-mono-data font-semibold text-gray-950" data-testid="receipt-claim-total">{fmtAUDex(claim.total_amount)}</span></div>
        </div>
        <Field label="Payment Received Date *"><input data-testid="receipt-date" type="date" required className="bdx-input" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <p className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] leading-relaxed text-gray-500">
          Confirming records the full claim value of {fmtAUDex(claim.total_amount)} Excl. GST as received. There is no partial-payment accounting.
        </p>
        <div className="mt-4 flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" data-testid="receipt-cancel" className="bdx-btn-secondary" onClick={onClose}>Cancel</button>
          <button data-testid="receipt-confirm" disabled={busy} className="bdx-btn-primary">{busy ? "Recording…" : "Confirm Receipt"}</button>
        </div>
      </form>
    </Modal>
  );
}

function ClaimInvoiceCell({ project, claim, docs, refreshDocs, readOnly }) {
  const items = (docs || []).filter((d) => d.linked_to === `claim:${claim.id}`);
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [listOpen, setListOpen] = useState(false);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (items.length + list.length > MAX_CLAIM_DOCS) {
      toast.error(`A claim supports up to ${MAX_CLAIM_DOCS} invoice attachments (${items.length} already attached)`);
      return;
    }
    setBusy(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", "Claim Invoice");
        fd.append("linked_to", `claim:${claim.id}`);
        await api.post(`/projects/${project.id}/documents`, fd);
      }
      toast.success(`${list.length} invoice document${list.length > 1 ? "s" : ""} attached to ${claim.code}`);
      refreshDocs?.();
    } catch (e) { toast.error(apiError(e)); } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (d) => {
    if (!window.confirm("Delete this invoice attachment?")) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Invoice attachment deleted"); refreshDocs?.(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const view = () => (items.length === 1 ? setPreview(items[0]) : setListOpen(true));

  return (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()} data-testid={`claim-invoice-${claim.code}`}>
      {items.length > 0 && (
        <button data-testid={`claim-view-${claim.code}`} className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-blue-300 bg-blue-50 px-2.5 py-1 text-[10px] font-semibold uppercase text-blue-700 transition-colors hover:bg-blue-100" style={{ letterSpacing: "0.05em" }}
          title={items.length === 1 ? `View ${items[0].filename}` : `View ${items.length} invoice documents`} onClick={view}>
          View{items.length > 1 && <span className="font-mono-data normal-case">· {items.length}</span>}
        </button>
      )}
      <span className="font-mono-data" data-testid={`claim-invoice-ref-${claim.code}`}>{claim.invoice_reference || "—"}</span>
      {!readOnly && (
        <>
          <input ref={inputRef} data-testid={`claim-invoice-input-${claim.code}`} type="file" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
          <button data-testid={`claim-invoice-add-${claim.code}`} className="bdx-icon-btn h-8 w-8 disabled:opacity-40" disabled={busy || items.length >= MAX_CLAIM_DOCS}
            title={items.length >= MAX_CLAIM_DOCS ? `Maximum ${MAX_CLAIM_DOCS} invoice attachments` : "Attach invoice document"} onClick={() => inputRef.current?.click()}>
            <Upload size={14} strokeWidth={1.6} />
          </button>
        </>
      )}
      {listOpen && (
        <Modal title={`${claim.code} — Invoice Documents`} onClose={() => setListOpen(false)}>
          <ul className="divide-y divide-gray-100" data-testid={`claim-invoice-list-${claim.code}`}>
            {items.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <button className="block max-w-full truncate text-left font-mono-data text-xs font-medium text-gray-800 hover:text-[#604184] hover:underline" onClick={() => setPreview(d)} data-testid={`claim-invoice-open-${d.id}`}>{d.filename}</button>
                  <div className="text-[10px] text-gray-500">{fmtDate(d.uploaded_at)} · {d.uploaded_by}</div>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  <button className="inline-flex items-center rounded border border-blue-300 bg-blue-50 px-2 py-1 text-[10px] font-semibold uppercase text-blue-700 hover:bg-blue-100" title="View" onClick={() => setPreview(d)} data-testid={`claim-invoice-view-${d.id}`}>View</button>
                  <button className="bdx-icon-btn h-7 w-7" title="Download" onClick={() => downloadBlob(`/documents/${d.id}/download`, d.filename)} data-testid={`claim-invoice-download-${d.id}`}><Download size={13} strokeWidth={1.6} /></button>
                  {!readOnly && <button className="bdx-icon-btn-danger h-7 w-7" title="Delete" onClick={() => remove(d)} data-testid={`claim-invoice-delete-${d.id}`}><Trash2 size={13} strokeWidth={1.6} /></button>}
                </div>
              </li>
            ))}
          </ul>
        </Modal>
      )}
      {preview && <DocPreview doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function SummaryRow({ label, value, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 first:pt-0">
      <dt className="shrink-0 text-gray-500">{label}</dt>
      <dd data-testid={testId} className="text-right font-medium text-gray-950">{value}</dd>
    </div>
  );
}

function ClaimSummaryDialog({ claim, docs, onClose }) {
  const items = (docs || []).filter((d) => d.linked_to === `claim:${claim.id}`);
  const [preview, setPreview] = useState(null);
  const payTone = claim.paid ? "green" : claim.sent ? "green" : "grey";
  const payStatus = claim.paid ? "Received" : claim.sent ? "Sent" : "Not Sent";

  return (
    <Modal title={`Claim Summary — ${claim.code}`} onClose={onClose}>
      <div data-testid={`claim-summary-${claim.code}`}>
        <div className="mb-4 flex items-center justify-between rounded-md border border-[#d9cde5] bg-[#f3eff7] px-4 py-3">
          <div>
            <div className="text-[10px] font-semibold uppercase text-[#604184]" style={{ letterSpacing: "0.1em" }}>Total Claim Value (Excl. GST)</div>
            <div data-testid="claim-summary-total" className="mt-0.5 font-mono-data text-lg font-bold text-gray-950">{fmtAUDex(claim.total_amount)}</div>
          </div>
          <StatusBadge status={payStatus} tone={payTone} testId="claim-summary-payment-status" />
        </div>
        <dl className="divide-y divide-gray-100 text-sm">
          <SummaryRow label="Claim Number" value={<span className="font-mono-data">{claim.code}</span>} testId="claim-summary-number" />
          <SummaryRow label="Claim Date" value={<span className="font-mono-data">{fmtDate(claim.claim_date)}</span>} testId="claim-summary-date" />
          <SummaryRow label="Base Progress Movement" value={<span className="font-mono-data">{claim.previous_pct}% → {claim.progress_pct}%</span>} testId="claim-summary-progress" />
          <SummaryRow label="Base Value" value={<span className="font-mono-data">{fmtAUDex(claim.base_amount)}</span>} testId="claim-summary-base" />
          <SummaryRow label="Variations Included" value={<span className="font-mono-data text-xs">{claim.variations?.map((v) => v.code).join(", ") || "—"}</span>} testId="claim-summary-variations" />
          <SummaryRow label="Variations Value" value={<span className="font-mono-data">{fmtAUDex(claim.variations_amount)}</span>} testId="claim-summary-variations-value" />
          <SummaryRow label="Invoice Reference" value={<span className="font-mono-data">{claim.invoice_reference || "—"}</span>} testId="claim-summary-invoice-ref" />
          {claim.invoice_date && <SummaryRow label="Invoice Date" value={<span className="font-mono-data">{fmtDate(claim.invoice_date)}</span>} testId="claim-summary-invoice-date" />}
          {claim.paid_at && <SummaryRow label="Payment Received" value={<span className="font-mono-data">{fmtDate(claim.paid_at)}{claim.paid_recorded_by ? ` · ${claim.paid_recorded_by}` : ""}</span>} testId="claim-summary-received" />}
        </dl>
        <div className="mt-4">
          <div className="bdx-label mb-1.5">Notes</div>
          <p data-testid="claim-summary-notes" className="whitespace-pre-wrap rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm leading-relaxed text-gray-700">{claim.notes || "—"}</p>
        </div>
        <div className="mt-4">
          <div className="bdx-label mb-1.5">Invoice Attachments</div>
          {items.length === 0 ? (
            <p data-testid="claim-summary-no-attachments" className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-2.5 text-xs text-gray-400">No invoice documents attached</p>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-md border border-gray-200" data-testid="claim-summary-attachments">
              {items.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <button className="block max-w-full truncate text-left font-mono-data text-xs font-medium text-gray-800 hover:text-[#604184] hover:underline" onClick={() => setPreview(d)} data-testid={`claim-summary-doc-open-${d.id}`}>{d.filename}</button>
                    <div className="text-[10px] text-gray-500">{fmtDate(d.uploaded_at)} · {d.uploaded_by}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button className="inline-flex items-center rounded border border-blue-300 bg-blue-50 px-2 py-1 text-[10px] font-semibold uppercase text-blue-700 hover:bg-blue-100" title="View" onClick={() => setPreview(d)} data-testid={`claim-summary-doc-view-${d.id}`}>View</button>
                    <button className="bdx-icon-btn h-7 w-7" title="Download" onClick={() => downloadBlob(`/documents/${d.id}/download`, d.filename)} data-testid={`claim-summary-doc-download-${d.id}`}><Download size={13} strokeWidth={1.6} /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="mt-4 flex justify-end border-t border-gray-100 pt-3">
          <button type="button" data-testid="claim-summary-close" className="bdx-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
      {preview && <DocPreview doc={preview} onClose={() => setPreview(null)} />}
    </Modal>
  );
}

export default function ClaimsTab({ project, docs, refreshDocs }) {
  const { readOnly } = useAuth();
  const [claims, setClaims] = useState(null);
  const [variations, setVariations] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [receiving, setReceiving] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    api.get(`/projects/${project.id}/claims`).then((r) => setClaims(r.data));
    api.get(`/projects/${project.id}/variations`).then((r) => setVariations(r.data));
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const removeClaim = async (c) => {
    const warn = c.paid
      ? `Delete ${c.code}? WARNING: this claim has been PAID — the recorded payment receipt (${fmtDate(c.paid_at)}) will also be removed. Included variations will return to Unclaimed.`
      : `Delete ${c.code}? Included variations will return to Unclaimed. Only the most recent claim can be deleted.`;
    if (!window.confirm(warn)) return;
    try { await api.delete(`/claims/${c.id}`); toast.success(`${c.code} deleted`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const currentPct = claims?.length ? claims[claims.length - 1].progress_pct : 0;

  return (
    <div data-testid="claims-tab" className="bdx-page-enter">
      <div className="mb-5 flex flex-col gap-4 border-b border-gray-300 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="bdx-icon-tile"><ReceiptText size={16} strokeWidth={1.9} /></div>
          <div>
            <h2 className="font-heading text-lg font-bold text-gray-950">Contract Progress Claims</h2>
            <p className="mt-1 text-[11px] text-gray-500">All values Excl. GST · Base Contract progresses 0% → 100% and never exceeds 100%</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-3 rounded-lg border border-[#d9cde5] bg-[#f3eff7] px-4 py-2.5 shadow-sm">
            <Gauge size={17} strokeWidth={1.8} className="text-[#604184]" />
            <div>
            <div className="bdx-label">Base Contract Claimed</div>
            <div data-testid="claims-current-pct" className="font-mono-data font-semibold text-gray-900">{currentPct}%</div>
            </div>
          </div>
          {!readOnly && (
            <button data-testid="new-claim-btn" className="bdx-btn-primary" onClick={() => setShowNew(true)}>
              <Plus size={15} strokeWidth={2} /> New Claim
            </button>
          )}
        </div>
      </div>

      <div className="mb-5 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2"><BadgeDollarSign size={15} strokeWidth={1.8} className="text-[#604184]" /><span className="bdx-label">Base Contract Progress</span></div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-200">
          <div className="h-full bg-[#765297] transition-[width] duration-300" style={{ width: `${Math.min(100, currentPct)}%` }} />
        </div>
        <div className="text-[11px] text-gray-500 mt-1 font-mono-data">Base Contract Progress: {currentPct}% of 100%</div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-[0_3px_12px_rgba(16,19,21,0.055)]">
        <table className="bdx-table" data-testid="claims-table">
          <thead><tr><th>No.</th><th>Date</th><th>Base Progress</th><th>Base Value</th><th>Variations Included</th><th>Variations Value</th><th>Total (Excl. GST)</th><th>Payment</th><th>Invoice</th>{!readOnly && <th className="w-16">Actions</th>}</tr></thead>
          <tbody>
            {claims?.length === 0 && <tr><td colSpan={10} className="text-center text-gray-400 py-10">No claims recorded</td></tr>}
            {claims?.map((c, i) => (
              <tr key={c.id} data-testid={`claim-row-${c.code}`}
                onClick={() => setSummary(c)}
                className="cursor-pointer transition-colors duration-150 hover:bg-[#faf8fc]"
                title={`Open ${c.code} summary`}>
                <td className="font-mono-data font-medium">{c.code}</td>
                <td className="font-mono-data">{fmtDate(c.claim_date)}</td>
                <td className="font-mono-data">{c.previous_pct}% → {c.progress_pct}%</td>
                <td className="font-mono-data">{fmtAUDex(c.base_amount)}</td>
                <td className="font-mono-data text-xs">{c.variations.map((v) => v.code).join(", ") || "—"}</td>
                <td className="font-mono-data">{fmtAUDex(c.variations_amount)}</td>
                <td className="font-mono-data font-semibold">{fmtAUDex(c.total_amount)}</td>
                <td data-testid={`claim-payment-${c.code}`}><PaymentCell claim={c} readOnly={readOnly} onChanged={load} onReceive={setReceiving} /></td>
                <td><ClaimInvoiceCell project={project} claim={c} docs={docs} refreshDocs={refreshDocs} readOnly={readOnly} /></td>
                {!readOnly && (
                  <td onClick={(e) => e.stopPropagation()}>
                    {i === claims.length - 1 && (
                      <button data-testid={`claim-delete-${c.code}`} className="bdx-icon-btn-danger" onClick={() => removeClaim(c)} aria-label={`Delete ${c.code}`}><Trash2 size={14} strokeWidth={1.7} /></button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && claims && (
        <NewClaimDialog project={project} claims={claims} variations={variations}
          onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />
      )}
      {receiving && (
        <ReceiptDialog claim={receiving} onClose={() => setReceiving(null)} onSaved={() => { setReceiving(null); load(); }} />
      )}
      {summary && (
        <ClaimSummaryDialog claim={summary} docs={docs} onClose={() => setSummary(null)} />
      )}
    </div>
  );
}
